/* eslint-disable react-refresh/only-export-components */
/**
 * ScopeContext - Provides current scope state across the application
 *
 * The scope determines what data context the user is viewing.
 * This context derives scope from:
 * - URL path (e.g., /w/:clientSlug → client_scoped)
 * - URL query param (e.g., /actions?client=dallas-life → client_scoped)
 * - ClientContext state
 *
 * Used by:
 * - GlobalHeader to show current scope
 * - ActionsPage to filter by scope
 * - Confirmation dialogs to include client name
 */

import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useClient } from "./ClientContext";
import { getClientSlug } from "../types/client";
import {
  type Scope,
  type ScopeLevel,
  createAllClientsScope,
  createClientScopeFromRoute,
  createClientScopeFromQuery,
  getDestructiveActionConfirmation,
  getScopeLabel,
} from "../types/scope";

// =============================================================================
// Context Type
// =============================================================================

interface ScopeContextValue {
  /** Current scope (derived from URL and client state) */
  scope: Scope;

  /** Whether we're at platform level (all clients) */
  isAllClients: boolean;

  /** Whether we're scoped to a specific client */
  isClientScoped: boolean;

  /** Whether we're scoped to a specific campaign */
  isCampaignScoped: boolean;

  /** Whether we're on a platform page (not /w/:clientSlug) */
  isPlatformPage: boolean;

  /** Get display label for current scope */
  scopeLabel: string;

  /** Get confirmation message for destructive action */
  getConfirmation: (actionDescription: string) => string;

  /** Set scope to a specific client (emits canonical /w/<slug>/... URLs) */
  setClientScope: (clientSlug: string) => void;

  /** Clear client scope (removes ?client= param) */
  clearScope: () => void;

  /** Invalid client slug from URL (not found or unauthorized) */
  unknownClientSlug: string | null;
}

const ScopeCtx = createContext<ScopeContextValue | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

interface ScopeProviderProps {
  children: ReactNode;
}

export function ScopeProvider({ children }: ScopeProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams<{ clientSlug?: string; campaignId?: string }>();
  const { currentClient, clients } = useClient();

  // Check if we're on a platform page (not a workspace route)
  const isPlatformPage = !location.pathname.startsWith("/w/");

  // Get client from ?client= query param (for platform pages only)
  const clientSlugParam = searchParams.get("client");
  const clientFromParam = useMemo(() => {
    // Only consider query param on platform pages
    // Rule: workspace route always wins over query param
    if (!clientSlugParam || !isPlatformPage) return null;
    // Find client by slug (case-insensitive match on name-derived slug)
    return clients.find((c) => getClientSlug(c) === clientSlugParam) ?? null;
  }, [clientSlugParam, isPlatformPage, clients]);

  // Derive scope from URL and client state
  // Precedence: route > query param > all_clients
  const scope = useMemo((): Scope => {
    const path = location.pathname;

    // 1. Workspace route (/w/:clientSlug/*) - highest precedence
    // This scope wins regardless of any query params
    if (path.startsWith("/w/") && params.clientSlug && currentClient) {
      const slug = getClientSlug(currentClient);
      // Check if we're in a campaign context
      if (params.campaignId) {
        // For now, treat campaign routes as client-scoped
        // Campaign scope will be implemented in future sprint
        return createClientScopeFromRoute(
          currentClient.id,
          currentClient.name,
          slug,
        );
      }

      return createClientScopeFromRoute(
        currentClient.id,
        currentClient.name,
        slug,
      );
    }

    // 2. Platform page with ?client= query param
    if (isPlatformPage && clientFromParam) {
      const slug = getClientSlug(clientFromParam);
      return createClientScopeFromQuery(
        clientFromParam.id,
        clientFromParam.name,
        slug,
      );
    }

    // 3. Default: platform-level without client scope
    return createAllClientsScope();
  }, [
    location.pathname,
    params.clientSlug,
    params.campaignId,
    currentClient,
    isPlatformPage,
    clientFromParam,
  ]);

  // Derived booleans for convenience
  const isAllClients = scope.level === "all_clients";
  const isClientScoped = scope.level === "client_scoped";
  const isCampaignScoped = scope.level === "campaign_scoped";

  // Detect unknown client slug (param provided but client not found)
  const unknownClientSlug = useMemo(() => {
    // Only relevant on platform pages with a client param that didn't resolve
    if (!isPlatformPage || !clientSlugParam) return null;
    if (clientFromParam) return null; // Found the client
    return clientSlugParam; // Slug was provided but not found
  }, [isPlatformPage, clientSlugParam, clientFromParam]);

  // Get current scope label
  const scopeLabel = useMemo(() => getScopeLabel(scope), [scope]);

  // Get confirmation message for destructive actions
  const getConfirmation = useCallback(
    (actionDescription: string) =>
      getDestructiveActionConfirmation(scope, actionDescription),
    [scope],
  );

  // Set scope to a specific client (emits canonical /w/<slug>/... URLs)
  const setClientScope = useCallback(
    (clientSlug: string) => {
      const path = location.pathname;
      if (path === "/actions") {
        // Mode-dual route: navigate to /w/<slug>/actions
        void navigate(`/w/${clientSlug}${path}`);
        return;
      }
      if (path.startsWith("/w/")) {
        // Already on workspace: swap the slug in the path
        const segments = path.split("/").slice(3);
        void navigate(`/w/${clientSlug}/${segments.join("/")}`);
        return;
      }
      // Portfolio-only or unknown: navigate to workspace root
      void navigate(`/w/${clientSlug}/`);
    },
    [navigate, location.pathname],
  );

  // Clear client scope (removes ?client= param)
  const clearScope = useCallback(() => {
    if (!isPlatformPage) {
      // On workspace pages, navigate to platform home
      void navigate("/actions");
      return;
    }
    // On platform pages, remove ?client= param
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("client");
    const newSearch = newParams.toString();
    void navigate(
      newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
      { replace: true },
    );
  }, [isPlatformPage, navigate, searchParams, location.pathname]);

  const value = useMemo(
    (): ScopeContextValue => ({
      scope,
      isAllClients,
      isClientScoped,
      isCampaignScoped,
      isPlatformPage,
      scopeLabel,
      getConfirmation,
      setClientScope,
      clearScope,
      unknownClientSlug,
    }),
    [
      scope,
      isAllClients,
      isClientScoped,
      isCampaignScoped,
      isPlatformPage,
      scopeLabel,
      getConfirmation,
      setClientScope,
      clearScope,
      unknownClientSlug,
    ],
  );

  return <ScopeCtx.Provider value={value}>{children}</ScopeCtx.Provider>;
}

// =============================================================================
// Hook
// =============================================================================

export function useScope(): ScopeContextValue {
  const ctx = useContext(ScopeCtx);
  if (!ctx) {
    throw new Error("useScope must be used within ScopeProvider");
  }
  return ctx;
}

// =============================================================================
// Utility Hook for Scope Checks
// =============================================================================

/**
 * Hook to check if current scope matches expected level
 */
export function useScopeCheck(expectedLevel: ScopeLevel): boolean {
  const { scope } = useScope();
  return scope.level === expectedLevel;
}
